import { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { useFormik } from "formik";
import Drawer from "../../../../../../components/SideDrawer/SideDrawer";
import { CreateViewForm } from "./components/CreateViewForm";
import { CreateViewFilters } from "./components/CreateViewFilters";
import { CreateViewFiltersLoadingShimmer } from "./components/CreateViewFiltersLoadingShimmer";
import { ViewFormValues, INITIAL_VIEW_FORM_VALUES } from "./utils/constants";
import { mapHelpdeskViewToFormValues } from "./utils/mapHelpdeskViewToFormValues";
import {
  viewFormValidationSchema,
  buildFilterGroupsPayload,
} from "./utils/functions";
import { customToast } from "../../../../../../utils/custom-toast/custom-toast";
import {
  useCreateHelpdeskViewMutation,
  useGetHelpdeskCustomFieldsQuery,
  useGetHelpdeskViewQuery,
  useUpdateHelpdeskViewMutation,
} from "../../../../../../redux/services/helpdeskApi";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../../../../../../redux/store";
import { setNewlyCreatedViewId } from "../../../../../../redux/slices/helpdeskSlice";
import { clearDrawer } from "../../../../../../redux/slices/drawerSlice";
import type { HelpdeskCustomFieldDefinition } from "../TicketDetails/components/TicketOverview/utils/helpdeskCustomFields";

export const CreateEditView = () => {
  const dispatch = useDispatch();

  const drawerState = useSelector((state: RootState) => state.drawer);

  const isDrawerOpen = Boolean(
    drawerState?.open &&
    (drawerState?.type === "helpdesk_view" ||
      drawerState?.type === "helpdesk_edit_view")
  );
  const isEditMode = drawerState?.type === "helpdesk_edit_view";
  const editViewId = isEditMode ? drawerState?.id : undefined;

  const [step, setStep] = useState<"form" | "filters">("form");
  const [viewEmoji, setViewEmoji] = useState("");

  const [createHelpdeskView] = useCreateHelpdeskViewMutation();
  const [updateHelpdeskView] = useUpdateHelpdeskViewMutation();

  const { data: customFieldsData } = useGetHelpdeskCustomFieldsQuery({});

  const { data: editViewData, isFetching: isEditViewFetching } =
    useGetHelpdeskViewQuery(editViewId ?? "", {
      skip: !isEditMode || !editViewId,
    });

  const customFieldDefinitions = customFieldsData as
    | HelpdeskCustomFieldDefinition[]
    | undefined;

  // Stable key: only changes when the actual view data or custom fields change.
  // Prevents the form from re-populating on every render.
  const editViewDataKey = useMemo(() => {
    if (!isDrawerOpen || !isEditMode || !editViewId || !editViewData)
      return null;
    const customFieldCount = customFieldDefinitions?.length ?? 0;
    return `${editViewId}|${editViewData.updated_at ?? ""}|${customFieldCount}`;
  }, [
    isDrawerOpen,
    isEditMode,
    editViewId,
    editViewData,
    customFieldDefinitions,
  ]);

  const formik = useFormik<ViewFormValues>({
    initialValues: INITIAL_VIEW_FORM_VALUES,
    validationSchema: viewFormValidationSchema,
    onSubmit: async (values) => {
      const filterGroups = buildFilterGroupsPayload(
        values?.filter_groups,
        customFieldDefinitions
      );

      const payload = {
        name: values?.name,
        scope: values?.scope as "private" | "public",
        filter_groups: filterGroups,
        logical_operator: values?.logical_operator,
        emoji_icon: viewEmoji,
      };

      try {
        if (editViewId) {
          await updateHelpdeskView({
            viewId: editViewId,
            ...payload,
          }).unwrap();

          customToast("View updated successfully", "success");
        } else {
          const result = await createHelpdeskView(payload).unwrap();
          dispatch(setNewlyCreatedViewId(result?.id ?? result?._id ?? null));
          customToast("View created successfully", "success");
        }
        handleClose();
      } catch (error: unknown) {
        const errorMessage =
          (error as { data: { detail: string } })?.data?.detail ||
          "Failed to save view";
        customToast(errorMessage, "error", 3000, undefined, "bottom-left");
      }
    },
  });

  const handleClose = () => {
    setStep("form");
    setViewEmoji("");
    formik.resetForm({ values: INITIAL_VIEW_FORM_VALUES });
    dispatch(clearDrawer());
  };

  const handleNext = async () => {
    formik.setFieldTouched("name", true);
    formik.setFieldTouched("scope", true);
    const errors = await formik.validateForm();
    if (errors.name || errors.scope) return;
    setStep("filters");
  };

  // Reset form when "create view" drawer opens
  useEffect(() => {
    if (!isDrawerOpen || drawerState?.type !== "helpdesk_view") return;
    setStep("form");
    setViewEmoji("");
    formik.resetForm({ values: INITIAL_VIEW_FORM_VALUES });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen, drawerState?.type]);

  // Populate form when edit-view data arrives
  useEffect(() => {
    if (!editViewDataKey || !editViewData) return;
    const { values: formValues, emoji } = mapHelpdeskViewToFormValues(
      editViewData,
      customFieldDefinitions
    );
    formik.resetForm({ values: formValues });
    setViewEmoji(emoji);
    setStep("filters");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editViewDataKey]);

  const hasUnsavedChanges = useMemo(() => {
    const currentValues = formik.values;
    const initialValues = formik.initialValues;
    return (
      !_.isEqual(currentValues.filter_groups, initialValues.filter_groups) ||
      currentValues.logical_operator !== initialValues.logical_operator
    );
  }, [formik.values, formik.initialValues]);

  const showShimmer =
    isDrawerOpen &&
    isEditMode &&
    Boolean(editViewId) &&
    (!editViewData || isEditViewFetching);

  const scopeDisplay =
    formik.values.scope === "public"
      ? "everyone"
      : formik.values.scope === "private"
        ? "private"
        : "";

  return (
    <Drawer
      open={isDrawerOpen}
      onClose={handleClose}
      direction="right"
      size="550px"
      overlayColor="rgba(0,0,0,0.7)"
      duration={300}
      closeOnOverlayClick={true}
      closeOnEscape={true}
      drawerContainerClassName="rounded-l-[8px]"
    >
      {showShimmer ? (
        <CreateViewFiltersLoadingShimmer />
      ) : step === "form" ? (
        <CreateViewForm
          formTitle={isEditMode ? "Edit view" : "Create view"}
          viewName={formik.values.name}
          viewEmoji={viewEmoji}
          sharedWith={scopeDisplay}
          onViewNameChange={(name) => formik.setFieldValue("name", name)}
          onEmojiChange={setViewEmoji}
          onSharedWithChange={(value) =>
            formik.setFieldValue(
              "scope",
              value === "everyone" ? "public" : "private"
            )
          }
          scopeError={
            formik.touched.scope && formik.errors.scope
              ? formik.errors.scope
              : ""
          }
          onClose={handleClose}
          onNext={handleNext}
          isEditMode={isEditMode}
        />
      ) : (
        <CreateViewFilters
          filterGroups={formik.values.filter_groups}
          logicalOperator={formik.values.logical_operator}
          onFilterGroupsChange={(groups) =>
            formik.setFieldValue("filter_groups", groups)
          }
          onLogicalOperatorChange={(operator) =>
            formik.setFieldValue("logical_operator", operator)
          }
          onBack={() => setStep("form")}
          onClose={handleClose}
          onSave={() => formik.handleSubmit()}
          isEditMode={isEditMode}
          isSaveViewLoading={formik.isSubmitting}
          hasUnsavedChanges={hasUnsavedChanges}
        />
      )}
    </Drawer>
  );
};
